import { createContext, useContext, useState, useEffect } from 'react';
import { settingsApi } from '../lib/api';

const ThemeContext = createContext(null);

const DEFAULT_SETTINGS = {
  app_name: 'ResaWork',
  logo_url: '/logo.png',
  primary_color: '#8BC34A',
  secondary_color: '#FFFFFF',
  bg_color: '#F9F8F6',
  text_color: '#1a1a1a',
  footer_text: '© ResaWork',
};

// Convert hex to HSL for CSS variables
function hexToHSL(hex) {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse hex
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;

  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function ThemeProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await settingsApi.getPublic();
        if (response.data) {
          setSettings({ ...DEFAULT_SETTINGS, ...response.data });
        }
      } catch (error) {
        console.warn('Could not load theme settings, using defaults');
      } finally {
        setIsLoading(false);
      }
    };
    loadSettings();
  }, []);

  // Apply theme colors to CSS variables
  useEffect(() => {
    if (settings.primary_color) {
      const root = document.documentElement;
      
      // Convert and apply primary color
      const primaryHSL = hexToHSL(settings.primary_color);
      root.style.setProperty('--primary', primaryHSL);
      root.style.setProperty('--ring', primaryHSL);
      
      // Apply secondary color if different
      if (settings.secondary_color && settings.secondary_color !== '#FFFFFF') {
        const secondaryHSL = hexToHSL(settings.secondary_color);
        root.style.setProperty('--secondary', secondaryHSL);
      }
    }
  }, [settings]);

  const value = {
    settings,
    isLoading,
    appName: settings.app_name,
    logoUrl: settings.logo_url,
    footerText: settings.footer_text,
    primaryColor: settings.primary_color,
    secondaryColor: settings.secondary_color,
    appVersion: settings.app_version,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
